/**
 * PlayerIdentityBlockView - Pure presentation component for player identity info
 *
 * Displays player name, badges (role, valuation, loan), and tidbits
 * (positions, club, age, nationality, agency) in a structured layout.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { Footprints } from "lucide-react-native";
import { memo } from "react";

import LimitedDataWarningBadge from "@/components/molecules/LimitedDataWarningBadge";
import ManualPlayerBadge from "@/components/molecules/ManualPlayerBadge";
import { Badge, BadgeText } from "@/components/atoms/Badge";
import { Icon } from "@/components/atoms/Icon";
import { Image } from "@/components/atoms/Image";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import WatchlistPill from "@/features/player-detail/components/molecules/WatchlistPill";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

import { EMPTY_VALUE_PLACEHOLDER } from "@/features/player-detail/utils/playerPageHeaderConstants";
import type { PlayerIdentityBlockViewProps } from "@/features/player-detail/utils/playerPageHeaderTypes";

import TidbitWithSeparator, {
  formatAgeDisplay,
  getTidbitTextClassName,
} from "@/features/player-detail/utils/TidbitWithSeparator";

/**
 * Nationality flag image dimensions.
 * design-system: dynamic-style waiver — off-token flag aspect-ratio geometry (20×14)
 */
const FLAG_STYLE = { width: 20, height: 14 };

/**
 * Get container alignment and gap class. SE-5372 / V1.6 Figma: the hero identity
 * block is LEFT-aligned on every breakpoint (previously centred on desktop).
 * @param isCompact - Whether to use compact spacing
 */
const getContainerAlignClass = (isCompact: boolean): string =>
  `w-full ${isCompact ? "gap-1" : "gap-2"} items-start`;

/**
 * Player name text class. SE-5372 / V1.6 Figma: left-aligned on every breakpoint.
 */
const NAME_CLASS = "text-content-primary";

/**
 * Horizontal row class for the badge + tidbit rows. SE-5372 / V1.6 Figma:
 * left-aligned (start) on every breakpoint — previously centred on desktop.
 */
const ROW_ALIGN_CLASS = "flex-wrap items-center gap-2";

/** Maps preferred foot codes to their display labels */
const PREFERRED_FOOT_LABELS: Record<string, string> = {
  right: "R",
  left: "L",
} as const;

/**
 * Returns the single-character display label for a player's preferred foot
 * @param preferredFoot - The preferred foot value ("Right", "Left", or other)
 */
const getPreferredFootLabel = (preferredFoot: string): string =>
  PREFERRED_FOOT_LABELS[preferredFoot.toLowerCase()] ?? "Both";

/**
 * Watchlist indicator row (SE-5024; restyled in SE-5372 to match the V1.6
 * Figma). Renders "Saved in watchlists:" followed by a static pill per
 * watchlist, left-aligned, below the identity rows on desktop — including the
 * preview modal (SE-5450: the modal mirrors the full-page hero, so the compact
 * layout no longer suppresses the indicator). Hidden on small screens, where
 * SE-5084's mobile pill row takes over. Pills wrap rather than truncate.
 * @param root0 - Props
 * @param root0.watchlists - Watchlists this player is saved in
 * @param root0.isSmallScreen - Whether the layout is mobile sized
 */
const WatchlistIndicator = ({
  watchlists,
  isSmallScreen,
}: {
  readonly watchlists: readonly {
    readonly id: string;
    readonly name: string;
  }[];
  readonly isSmallScreen: boolean;
}): React.ReactNode => {
  // Width-gated, not platform-gated (screen-size-not-device-type rule): the
  // pill indicator renders on any non-small screen — including the compact
  // preview modal (SE-5450 parity with the full page). SE-5084 ships the mobile
  // (small-screen) pill row separately.
  if (watchlists.length === 0 || isSmallScreen) {
    return null;
  }

  return (
    <Stack direction="horizontal" className="flex-wrap items-center gap-2 py-2">
      <Text variant="body" className="text-content-muted">
        Saved in watchlists:
      </Text>
      {watchlists.map(watchlist => (
        <WatchlistPill
          key={watchlist.id}
          id={watchlist.id}
          name={watchlist.name}
        />
      ))}
    </Stack>
  );
};

/**
 * Mobile watchlist pill row (SE-5084). Renders below the identity rows on
 * mobile only — on desktop the plain-text WatchlistIndicator owns the surface.
 * SE-5450: shown in the compact/modal layout too (the modal mirrors the
 * full-page hero), gated purely on screen size. Behaviour per Notion 8.4:
 * - 0 lists → hidden
 * - 1 list → a single non-interactive pill
 * - 2+ lists → tappable pills; each opens the list-membership editor sheet
 *   via onWatchlistPress. Pills wrap rather than truncate.
 * @param root0 - Props
 * @param root0.watchlists - Watchlists this player is saved in
 * @param root0.isSmallScreen - Whether the layout is mobile sized
 * @param root0.onWatchlistPress - Tap handler for the 2+ list interactive pills
 */
const MobileWatchlistPills = ({
  watchlists,
  isSmallScreen,
  onWatchlistPress,
}: {
  readonly watchlists: readonly {
    readonly id: string;
    readonly name: string;
  }[];
  readonly isSmallScreen: boolean;
  readonly onWatchlistPress?: (watchlistId: string) => void;
}): React.ReactNode => {
  // Mobile-only surface; on desktop the plain-text indicator owns it. SE-5450:
  // rendered in the compact/modal layout too — gated purely on screen size.
  if (watchlists.length === 0 || !isSmallScreen) return null;

  // A single membership renders a non-interactive pill (Notion 8.4); 2+ become
  // tappable so the user can edit which lists the player belongs to.
  const isInteractive = watchlists.length > 1;

  return (
    <Stack direction="horizontal" className="flex-wrap items-center gap-2 pt-1">
      {watchlists.map(watchlist => (
        <WatchlistPill
          key={watchlist.id}
          id={watchlist.id}
          name={watchlist.name}
          onPress={isInteractive ? onWatchlistPress : undefined}
        />
      ))}
    </Stack>
  );
};

/**
 * Player name + primary role-archetype flag row.
 *
 * The name and the primary role-archetype badge sit inline on one row (testID
 * `player-identity-name-row`) so the flag reads beside the name on every
 * breakpoint — full-page hero and compact preview modal alike. Positions,
 * secondary badges and the metadata tidbits flow on the rows beneath (per
 * Raniel's V1.6 profile reference; supersedes the SE-5372 hero split that
 * dropped the badge to its own row on the full page).
 * @param root0 - Props
 * @param root0.playerName - Player's full display name
 * @param root0.roleArchetype - Primary tactical role archetype (null hides the inline badge)
 */
const PlayerNameRow = ({
  playerName,
  roleArchetype,
}: {
  readonly playerName: string;
  readonly roleArchetype: string | null;
}): React.ReactNode => {
  return (
    <Stack
      direction="horizontal"
      testID="player-identity-name-row"
      className="w-full items-center gap-2"
    >
      <Text
        {...{ "data-testid": TEST_ID_PLAYER_DETAIL.PLAYER_NAME }}
        variant="display-md"
        bold
        className={`${NAME_CLASS} min-w-0 flex-shrink`}
      >
        {playerName}
      </Text>
      {roleArchetype ? (
        <Badge action="info">
          <BadgeText>{roleArchetype}</BadgeText>
        </Badge>
      ) : null}
    </Stack>
  );
};

/**
 * PlayerIdentityBlockView renders the player identity section
 * @param root0 - View props
 * @param root0.playerName - Player's full display name for the header
 * @param root0.roleArchetype - Player's tactical role archetype (e.g., "Deep Playmaker")
 * @param root0.secondaryRoleArchetype - Player's secondary tactical role archetype. Accepted but never rendered: SE-7531 hides it at every screen size (SE-6992 had hidden it on small screens only)
 * @param root0.valuationStatus - Valuation status text for badge display (e.g., "UNDERVALUED")
 * @param root0.valuationBadgeAction - Badge color action based on valuation status
 * @param root0.onLoanFrom - Club name the player is on loan from, displayed uppercased in badge as "ON LOAN FROM [CLUB]"
 * @param root0.positions - Comma-separated position abbreviations (e.g., "CM, DM")
 * @param root0.clubName - Current club name for the tidbits section
 * @param root0.age - Player's age in years as a string
 * @param root0.birthYear - Player's birth year as a string (e.g., "1987")
 * @param root0.nationality - Player's nationality for display text
 * @param root0.nationalityFlagUrl - URL to the player's nationality flag image
 * @param root0.agency - Player's agency name for the tidbits section
 * @param root0.isInjured - Whether the player is currently injured
 * @param root0.injuredUntil - Date when the player's injury is expected to end (unused for now)
 * @param root0.preferredFoot - Player's preferred foot ("Right", "Left", or "Both")
 * @param root0.limitedDataWarning - Whether the player has limited data available, shows warning badge
 * @param root0.isManual - Whether the player was manually created by an organization user
 * @param root0.isDark - Whether dark mode is enabled, affects text colors
 * @param root0.isSmallScreen - Whether mobile sized, switches to left-aligned layout
 * @param root0.isCompact - Whether to use compact spacing (used in modal context)
 * @param root0.watchlists - Watchlists this player is saved in, for the SE-5024 indicator
 * @param root0.onWatchlistPress - Tap handler for the mobile interactive watchlist pills (SE-5084)
 * @returns VStack containing name, badges, and tidbits
 */
const PlayerIdentityBlockView = ({
  playerName,
  roleArchetype,
  secondaryRoleArchetype: _secondaryRoleArchetype,
  valuationStatus,
  valuationBadgeAction,
  onLoanFrom,
  positions,
  clubName,
  age,
  birthYear,
  nationality,
  nationalityFlagUrl,
  agency,
  isInjured,
  injuredUntil: _injuredUntil,
  preferredFoot,
  limitedDataWarning,
  isManual,
  isDark,
  isSmallScreen = false,
  isCompact = false,
  watchlists = [],
  onWatchlistPress,
}: PlayerIdentityBlockViewProps) => (
  <Stack className={getContainerAlignClass(isCompact)}>
    {/* The player name and primary role-archetype flag share one inline row on
        every breakpoint (V1.6 reference); positions and metadata flow beneath. */}
    <PlayerNameRow playerName={playerName} roleArchetype={roleArchetype} />

    {/* SE-5543: player positions render as plain text in the metadata tidbit
        row below (not as chips). Chips are reserved for the role-archetype
        badges (primary inline with the name, secondary on the row below). */}
    <Stack direction="horizontal" className={ROW_ALIGN_CLASS}>
      {/* The primary role-archetype badge is hoisted inline with the name
          (above) on every breakpoint; the status badges stay on this row.

          SE-7531: the secondary role archetype is no longer rendered at all.
          SE-6992 hid it on small screens behind a `!isSmallScreen` gate; this
          extends that to big/desktop screens too, which leaves no width where
          the badge should appear — so the gate is gone rather than widened.
          A conditional whose every branch resolves the same way is dead
          weight. `secondaryRoleArchetype` is still accepted (and still flows
          from the fragment) but is deliberately not rendered, following the
          `injuredUntil` precedent below. */}
      {valuationStatus ? (
        <Badge action={valuationBadgeAction}>
          <BadgeText>{valuationStatus}</BadgeText>
        </Badge>
      ) : null}
      {onLoanFrom ? (
        <Badge action="warning">
          <BadgeText>ON LOAN FROM {onLoanFrom.toUpperCase()}</BadgeText>
        </Badge>
      ) : null}
      {isInjured ? (
        <Badge action="error">
          <BadgeText>INJURED</BadgeText>
        </Badge>
      ) : null}
      <ManualPlayerBadge show={isManual} />
      <LimitedDataWarningBadge
        show={limitedDataWarning}
        isSmallScreen={false}
      />
    </Stack>

    <Stack direction="horizontal" className={ROW_ALIGN_CLASS}>
      {positions !== EMPTY_VALUE_PLACEHOLDER ? (
        <TidbitWithSeparator
          isDark={isDark}
          testID={TEST_ID_PLAYER_DETAIL.HEADER_POSITIONS}
        >
          {positions}
        </TidbitWithSeparator>
      ) : null}
      {clubName ? (
        <TidbitWithSeparator
          isDark={isDark}
          testID={TEST_ID_PLAYER_DETAIL.HEADER_CLUB}
        >
          {clubName}
        </TidbitWithSeparator>
      ) : null}
      {age ? (
        <TidbitWithSeparator isDark={isDark}>
          {formatAgeDisplay(age, birthYear)}
        </TidbitWithSeparator>
      ) : null}
      {nationalityFlagUrl ? (
        <Image
          source={{ uri: nationalityFlagUrl }}
          size="none"
          // design-system: dynamic-style — exact off-scale flag aspect-ratio
          // geometry (20×14) the closed sizing scale cannot express.
          // eslint-disable-next-line no-restricted-syntax -- bare re-export Image atom accepts style pass-through (no UNSAFE_style prop); off-scale 20×14 flag aspect-ratio geometry cannot be tokenized
          style={FLAG_STYLE}
          alt={`${nationality ?? "Country"} flag`}
        />
      ) : null}
      {nationality ? (
        <TidbitWithSeparator
          isDark={isDark}
          showSeparator={Boolean(preferredFoot || agency)}
        >
          {nationality}
        </TidbitWithSeparator>
      ) : null}
      {preferredFoot ? (
        <Stack direction="horizontal" className="items-center gap-1">
          <Icon
            as={Footprints}
            size="xs"
            className="text-content-muted"
            accessibilityLabel="Preferred foot"
          />
          <TidbitWithSeparator isDark={isDark} showSeparator={Boolean(agency)}>
            {getPreferredFootLabel(preferredFoot)}
          </TidbitWithSeparator>
        </Stack>
      ) : null}
      {agency ? (
        <Text variant="unstyled" className={getTidbitTextClassName(isDark)}>
          {agency}
        </Text>
      ) : null}
    </Stack>

    <WatchlistIndicator watchlists={watchlists} isSmallScreen={isSmallScreen} />

    <MobileWatchlistPills
      watchlists={watchlists}
      isSmallScreen={isSmallScreen}
      onWatchlistPress={onWatchlistPress}
    />
  </Stack>
);

PlayerIdentityBlockView.displayName = "PlayerIdentityBlockView";

export default memo(PlayerIdentityBlockView);
